import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  CalendarIcon, 
  ArrowDownTrayIcon, 
  ChevronLeftIcon, 
  ChevronRightIcon 
} from '@heroicons/react/24/outline';
import { useContent } from '../ContentContext';
import { SEND_EMAIL_URL } from '../config';
import { showToast } from './Toast';

const Hero = () => {
  const { content } = useContent();
  const { messages } = { messages: content };
  const sliders = (content as any).sliders || [];
  const [currentIdx, setCurrentIdx] = useState(0);
  const [formData, setFormData] = useState({ full_name: '', phone: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (sliders.length > 1) {
      const interval = setInterval(() => {
        setCurrentIdx(prev => (prev + 1) % sliders.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [sliders.length]);

  const activeSlider = sliders.length > 0 ? sliders[currentIdx] : null;

  return (
    <section id="home" className="relative min-h-screen flex items-center overflow-hidden py-20 lg:py-0">
      {/* Hero Background Image */}
      <div className="absolute inset-0 z-0 transition-opacity duration-1000">
        <img 
          src={activeSlider?.image_url || "/images/hero.jpeg"} 
          alt="Elite County" 
          className="w-full h-full object-cover transition-transform duration-[5000ms] scale-110"
          style={{ transform: 'scale(1.1)' }}
        />
        {/* Overlay for better text readability */}
        <div className="absolute inset-0 bg-black/40"></div>
      </div>

      <div className="container mx-auto px-4 sm:px-10 z-10 flex flex-col justify-center pt-16 lg:pt-20">
        <div className="grid lg:grid-cols-[1.2fr,0.8fr] gap-8 lg:gap-20 items-center w-full">
          <div className="text-white max-w-2xl">
            {activeSlider ? (
               <h1 className="text-3xl sm:text-5xl lg:text-7xl font-extrabold leading-[1.1] mb-6 lg:mb-8 drop-shadow-lg transition-all duration-700 transform translate-y-0 opacity-100">
               {activeSlider.title_prefix} <br />
               {activeSlider.title_middle} <br /><span className="text-[#EF5D22]">{activeSlider.title_highlight}</span> {activeSlider.title_suffix} <br />{activeSlider.title_county}
             </h1>
            ) : (
                <h1 className="text-3xl sm:text-5xl lg:text-7xl font-extrabold leading-[1.1] mb-6 lg:mb-8 drop-shadow-lg">
                {messages.hero.titlePrefix} <br />
                {messages.hero.titleMiddle} <br /><span className="text-[#EF5D22]">{messages.hero.titleHighlight}</span> {messages.hero.titleSuffix} <br />{messages.hero.titleCounty}
              </h1>
            )}
            <p className="text-base sm:text-lg lg:text-xl text-white font-medium mb-8 lg:mb-12 max-w-lg leading-relaxed drop-shadow-md">
              {activeSlider?.description || messages.hero.description}
            </p>
          </div>

          <div className="bg-white/90 backdrop-blur-sm p-6 sm:p-10 rounded-3xl shadow-2xl max-w-md mx-auto lg:ml-auto border border-white/20">
            <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">{messages.hero.formTitle}</h3>
            <p className="text-gray-700 mb-6 sm:mb-8 font-medium text-sm sm:text-base">{messages.hero.formSubtitle}</p>
            <form className="space-y-4 sm:space-y-5" onSubmit={async (e) => {
              e.preventDefault();
              if (!formData.full_name.trim() || !formData.phone.trim()) {
                showToast('Please fill in all fields', 'error');
                return;
              }
              if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) {
                showToast('Please enter a valid 10-digit phone number', 'error');
                return;
              }
              setIsSubmitting(true);
              try {
                const res = await fetch(SEND_EMAIL_URL, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  credentials: 'include',
                  body: JSON.stringify({
                    ...formData,
                    form_type: 'Pricing & Brochure Request',
                    source: 'Hero Section Form'
                  })
                });
                const json = await res.json();
                if (json.status === 'success') {
                  showToast('🎉 Thank you! Our team will contact you shortly with exclusive pricing details.', 'success');
                  setFormData({ full_name: '', phone: '' });
                } else {
                  showToast(json.message || 'Something went wrong. Please try again.', 'error');
                }
              } catch {
                showToast('Network error. Please check your connection and try again.', 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}>
              <div>
                <input 
                  type="text" 
                  placeholder={messages.hero.fullNamePlaceholder}
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  className="w-full px-4 sm:px-5 py-3 sm:py-4 bg-gray-100/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#EF5D22]/50 transition text-gray-900 text-sm sm:text-base"
                  disabled={isSubmitting}
                />
              </div>
              <div>
                <input 
                  type="tel" 
                  placeholder={messages.hero.phoneNumberPlaceholder}
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 sm:px-5 py-3 sm:py-4 bg-gray-100/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#EF5D22]/50 transition text-gray-900 text-sm sm:text-base"
                  disabled={isSubmitting}
                />
              </div>
              <button 
                type="submit"
                disabled={isSubmitting}
                className={`w-full bg-[#EF5D22] text-white py-4 sm:py-5 rounded-xl font-bold text-lg sm:text-xl shadow-xl transition duration-300 transform ${
                  isSubmitting ? 'opacity-70 cursor-not-allowed' : 'hover:bg-[#d9541a] hover:-translate-y-1'
                }`}
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                    Sending...
                  </span>
                ) : messages.hero.ctaButton}
              </button>
            </form>
          </div>
        </div>

        {/* Bottom Buttons and Arrows */}
        <div className="mt-8 lg:mt-0 lg:absolute lg:bottom-10 lg:left-10 lg:right-10 flex flex-col sm:flex-row justify-between items-center sm:items-end gap-6">
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full sm:w-auto">
            <Link to="/contact" className="bg-[#EF5D22] text-white px-6 sm:px-10 py-3 sm:py-4 rounded-xl font-bold text-base sm:text-lg flex items-center justify-center space-x-3 shadow-2xl hover:bg-[#d9541a] transition transform hover:-translate-y-1">
              <CalendarIcon className="w-5 h-5 sm:w-6 sm:h-6" />
              <span>{messages.hero.bookSiteVisit}</span>
            </Link>
            <a 
              href="/brochures/elite-county.pdf"
              download="Elite-County-Brochure.pdf"
              className="bg-white text-gray-900 px-6 sm:px-10 py-3 sm:py-4 rounded-xl font-bold text-base sm:text-lg flex items-center justify-center space-x-3 shadow-2xl hover:bg-gray-100 transition transform hover:-translate-y-1"
            >
              <ArrowDownTrayIcon className="w-5 h-5 sm:w-6 sm:h-6" />
              <span>{messages.hero.downloadBrochure}</span>
            </a>
          </div>

          {sliders.length > 1 && (
            <div className="flex space-x-3">
              <button 
                  onClick={() => setCurrentIdx(prev => (prev - 1 + sliders.length) % sliders.length)}
                  className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-black/40 text-white flex items-center justify-center hover:bg-black/60 transition backdrop-blur-md"
                  aria-label="Previous slide"
              >
                <ChevronLeftIcon className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
              <button 
                  onClick={() => setCurrentIdx(prev => (prev + 1) % sliders.length)}
                  className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-black/40 text-white flex items-center justify-center hover:bg-black/60 transition backdrop-blur-md"
                  aria-label="Next slide"
              >
                <ChevronRightIcon className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Hero;
